/**
 * PTE Practice Player — unified assessment shell.
 *
 * Dispatches renderer per question_type. Manages: current question index,
 * response state, submission, autoscore feedback, retake as new attempt.
 *
 * Architected so a future Simulation Mode can extend by locking navigation,
 * hiding feedback, and enforcing timers.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { pteApi, DISCLAIMER } from '@/lib/pteApi';
import RENDERERS from '@/components/pte/PTERenderers';
import { Loader2, ChevronRight, Home, CheckCircle2, XCircle, HelpCircle, Info, RotateCcw } from 'lucide-react';
import { toast } from 'sonner';
import Seo from '@/components/shared/Seo';

const modeLabel = (m) => m === 'simulation' ? 'Simulation' : 'Practice';

export default function PTEPracticePage() {
  const { taskType } = useParams();
  const [sp] = useSearchParams();
  const navigate = useNavigate();
  const [pool, setPool] = useState(null);
  const [idx, setIdx] = useState(0);
  const [meta, setMeta] = useState(null);
  const [strategy, setStrategy] = useState(null);
  const [response, setResponse] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [showStrategy, setShowStrategy] = useState(false);
  const mode = sp.get('mode') === 'simulation' ? 'simulation' : 'practice';

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [p, s] = await Promise.all([
          pteApi.pool(taskType, sp.get('difficulty') || null, Number(sp.get('count') || 5)),
          pteApi.strategy(taskType).catch(() => null),
        ]);
        if (cancelled) return;
        setPool(p.items || []);
        setMeta(p.meta || null);
        setStrategy(s?.strategy || null);
      } catch (e) {
        setError(e.response?.data?.detail || e.message);
      }
    })();
    return () => { cancelled = true; };
  }, [taskType, sp]);

  const q = pool && pool[idx];

  const submit = useCallback(async () => {
    if (!q) return;
    try {
      const payload = {
        question_id: q.id || q.question_id,
        response: response.response ?? null,
        recording_url: response.recording_url || null,
        mode,
      };
      const r = await pteApi.submit(payload);
      setResult(r);
      setSubmitted(true);
      if (r.xp_awarded > 0) toast.success(`+${r.xp_awarded} XP awarded`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Submit failed');
    }
  }, [q, response, mode]);

  const next = useCallback(() => {
    setResponse({}); setResult(null); setSubmitted(false);
    if (idx + 1 < (pool?.length || 0)) setIdx(idx + 1);
    else navigate('/resources/pte-academic');
  }, [idx, pool, navigate]);

  const retake = useCallback(() => {
    setResponse({}); setResult(null); setSubmitted(false);
  }, []);

  if (error) {
    return <div className="min-h-screen flex items-center justify-center text-red-600">{error}</div>;
  }
  if (!pool) {
    return (
      <div className="min-h-screen flex items-center justify-center gap-2 text-slate-500">
        <Loader2 className="h-5 w-5 animate-spin" /> Preparing practice…
      </div>
    );
  }
  if (!pool.length) {
    return (
      <div className="min-h-screen flex items-center justify-center flex-col gap-3 text-slate-600">
        <div>No published questions for this task type yet.</div>
        <button onClick={() => navigate('/resources/pte-academic')} className="px-4 py-2 rounded-full bg-slate-900 text-white text-sm">Back</button>
      </div>
    );
  }

  const Renderer = RENDERERS[q.question_type];

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="pte-practice-player">
      <Seo title={`${meta?.label || 'PTE'} · PTE Practice · Winhouse`} canonical={`/resources/pte-academic/practice/${taskType}`} />
      <div className="max-w-5xl mx-auto px-4 md:px-6 py-6">
        <div className="flex items-center justify-between gap-3 mb-5 flex-wrap">
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/resources/pte-academic')}
              className="w-8 h-8 rounded-full bg-white border border-slate-200 hover:bg-slate-50 flex items-center justify-center"
              data-testid="pte-back-home"><Home className="h-4 w-4" /></button>
            <div>
              <div className="text-xs uppercase tracking-widest text-slate-500">
                {modeLabel(mode)} · {meta?.section?.replace('_', ' & ')}
              </div>
              <div className="text-lg md:text-xl font-semibold text-slate-900">{meta?.label}</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="text-sm text-slate-600 font-mono" data-testid="pte-progress">
              Q{idx + 1} / {pool.length}
            </div>
            {strategy && (
              <button onClick={() => setShowStrategy((v) => !v)}
                data-testid="pte-strategy-toggle"
                className="px-3 py-1.5 rounded-full bg-white border border-slate-200 hover:border-slate-400 text-xs font-semibold inline-flex items-center gap-1">
                <HelpCircle className="h-3.5 w-3.5" /> Strategy
              </button>
            )}
          </div>
        </div>

        {showStrategy && strategy && (
          <div className="rounded-2xl bg-slate-900 text-white p-6 mb-5" data-testid="pte-strategy-panel">
            <div className="text-xs uppercase tracking-widest text-slate-400 mb-1">Strategy</div>
            <div className="text-base font-semibold mb-1">{strategy.what}</div>
            <div className="text-sm text-slate-300 mb-3">{strategy.skill}</div>
            <ol className="list-decimal list-inside text-sm space-y-1 mb-3">
              {(strategy.steps || []).map((s, i) => <li key={i}>{s}</li>)}
            </ol>
            <div className="grid md:grid-cols-2 gap-3 text-sm">
              <div><span className="text-slate-400 uppercase text-[10px] tracking-wider">Timing</span><div>{strategy.timing}</div></div>
              <div><span className="text-slate-400 uppercase text-[10px] tracking-wider">Tips</span><ul className="list-disc list-inside">{(strategy.tips || []).map((t, i) => <li key={i}>{t}</li>)}</ul></div>
            </div>
          </div>
        )}

        {Renderer ? (
          <Renderer question={q} onResponseChange={setResponse} disabled={submitted} />
        ) : (
          <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-amber-800">
            Renderer for <code>{q.question_type}</code> is not available yet.
          </div>
        )}

        {result && (
          <div className={`mt-4 rounded-2xl p-5 ${result.attempt?.requires_review ? 'bg-slate-100' : (result.attempt?.score === result.attempt?.max_score && result.attempt?.max_score ? 'bg-emerald-50 border border-emerald-200' : 'bg-amber-50 border border-amber-200')}`}
            data-testid="pte-feedback">
            <div className="flex items-center gap-2 font-semibold mb-2">
              {result.attempt?.requires_review ? (
                <><Info className="h-4 w-4" /> Submitted for tutor review</>
              ) : (result.attempt?.score === result.attempt?.max_score && result.attempt?.max_score) ? (
                <><CheckCircle2 className="h-4 w-4 text-emerald-600" /> Correct — well done</>
              ) : (
                <><XCircle className="h-4 w-4 text-amber-600" /> Partially correct</>
              )}
            </div>
            <div className="text-sm text-slate-700">{result.attempt?.feedback}</div>
            {result.attempt?.score !== null && result.attempt?.score !== undefined && (
              <div className="mt-2 text-xs text-slate-500 font-mono">
                Score: {result.attempt.score} / {result.attempt.max_score} · <em>Winhouse Estimated PTE Practice Score</em>
              </div>
            )}
          </div>
        )}

        <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
          <div className="text-[10px] text-slate-400 uppercase tracking-wider max-w-md">{DISCLAIMER}</div>
          <div className="flex gap-2">
            {!submitted ? (
              <button onClick={submit}
                data-testid="pte-submit"
                className="px-6 py-2.5 rounded-full bg-slate-900 hover:bg-slate-700 text-white font-semibold text-sm inline-flex items-center gap-2">
                Submit response <ChevronRight className="h-4 w-4" />
              </button>
            ) : (
              <>
                <button onClick={retake}
                  data-testid="pte-retake"
                  className="px-4 py-2 rounded-full bg-white border border-slate-200 hover:border-slate-400 text-sm inline-flex items-center gap-1.5">
                  <RotateCcw className="h-4 w-4" /> Retake
                </button>
                <button onClick={next}
                  data-testid="pte-next"
                  className="px-6 py-2.5 rounded-full bg-emerald-500 hover:bg-emerald-600 text-black font-semibold text-sm inline-flex items-center gap-2">
                  {idx + 1 < pool.length ? 'Next question' : 'Finish session'} <ChevronRight className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
